import { useState } from 'react'
import { Button, Group, Modal, Stack, Text, Textarea } from '@mantine/core'
import { IconBan, IconCheck, IconMessageExclamation } from '@tabler/icons-react'
import { MOTIVO_MIN, exigeMotivo, puedeTransicionar, type Estado } from '@serkat/shared'

interface Props {
  estado: Estado
  ocupado: boolean
  onTransicionar: (destino: Estado, motivo: string | null) => void
}

const ACCIONES = [
  { destino: 'Aprobado' as const, etiqueta: 'Aprobar', color: 'teal', icono: IconCheck },
  { destino: 'Observado' as const, etiqueta: 'Observar', color: 'blue', icono: IconMessageExclamation },
  { destino: 'Anulado' as const, etiqueta: 'Anular', color: 'red', icono: IconBan },
]

/**
 * Solo se ofrecen las transiciones válidas desde el estado actual. Esto es
 * comodidad, no seguridad: el backend y la base rechazan igual lo inválido.
 */
export function AccionesEstado({ estado, ocupado, onTransicionar }: Props) {
  const [destino, setDestino] = useState<Estado | null>(null)
  const [motivo, setMotivo] = useState('')

  const disponibles = ACCIONES.filter((a) => puedeTransicionar(estado, a.destino))
  if (disponibles.length === 0) {
    return <Text size="sm" c="dimmed">Sin acciones disponibles</Text>
  }

  function iniciar(d: Estado) {
    if (exigeMotivo(d)) {
      setDestino(d)
      setMotivo('')
    } else {
      onTransicionar(d, null)
    }
  }

  function confirmar() {
    if (!destino) return
    onTransicionar(destino, motivo.trim())
    setDestino(null)
  }

  const motivoCorto = motivo.trim().length < MOTIVO_MIN

  return (
    <>
      <Group gap="xs">
        {disponibles.map(({ destino: d, etiqueta, color, icono: Icono }) => (
          <Button
            key={d} size="xs" variant="light" color={color}
            leftSection={<Icono size={14} />}
            loading={ocupado}
            onClick={() => iniciar(d)}
          >
            {etiqueta}
          </Button>
        ))}
      </Group>

      <Modal
        opened={destino !== null}
        onClose={() => setDestino(null)}
        title={destino === 'Observado' ? 'Observar documento' : 'Anular documento'}
        size="md"
      >
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            {destino === 'Observado'
              ? 'El documento vuelve a Procura para su corrección. El motivo queda registrado.'
              : 'La anulación no se puede deshacer. El motivo queda registrado.'}
          </Text>
          <Textarea
            label="Motivo"
            withAsterisk
            autosize
            minRows={3}
            value={motivo}
            onChange={(e) => setMotivo(e.currentTarget.value)}
            error={motivoCorto && motivo.length > 0 ? `Mínimo ${MOTIVO_MIN} caracteres` : undefined}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDestino(null)}>Cancelar</Button>
            <Button
              color={destino === 'Anulado' ? 'red' : 'blue'}
              disabled={motivoCorto}
              loading={ocupado}
              onClick={confirmar}
            >
              {destino === 'Observado' ? 'Observar' : 'Anular'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  )
}
